import { useState } from 'react';
import { MessageSquare, Plus, Trash2, Copy, Check, Image, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';

interface EmbedField {
  name: string;
  value: string;
  inline: boolean;
}

interface Embed {
  title: string;
  description: string;
  color: string;
  url: string;
  thumbnail: string;
  image: string;
  footer: string;
  footerIcon: string;
  author: string;
  authorIcon: string;
  fields: EmbedField[];
}

const defaultEmbed: Embed = {
  title: '',
  description: '',
  color: '#5865F2',
  url: '',
  thumbnail: '',
  image: '',
  footer: '',
  footerIcon: '',
  author: '',
  authorIcon: '',
  fields: [],
};

const DiscordEmbedBuilder = () => {
  useToolUsage('discord_embed'); // Track tool usage

  const [embed, setEmbed] = useState<Embed>(defaultEmbed);
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  const updateEmbed = (key: keyof Embed, value: any) => {
    setEmbed({ ...embed, [key]: value });
  };

  const addField = () => {
    setEmbed({
      ...embed,
      fields: [...embed.fields, { name: '', value: '', inline: false }],
    });
  };

  const updateField = (index: number, key: keyof EmbedField, value: any) => {
    const newFields = [...embed.fields];
    newFields[index] = { ...newFields[index], [key]: value };
    setEmbed({ ...embed, fields: newFields });
  };

  const removeField = (index: number) => {
    setEmbed({
      ...embed,
      fields: embed.fields.filter((_, i) => i !== index),
    });
  };

  const generateJson = () => {
    const embedObject: any = {};

    if (embed.title) embedObject.title = embed.title;
    if (embed.description) embedObject.description = embed.description;
    if (embed.url) embedObject.url = embed.url;
    if (embed.color) embedObject.color = parseInt(embed.color.replace('#', ''), 16);
    if (embed.thumbnail) embedObject.thumbnail = { url: embed.thumbnail };
    if (embed.image) embedObject.image = { url: embed.image };
    if (embed.footer || embed.footerIcon) {
      embedObject.footer = {};
      if (embed.footer) embedObject.footer.text = embed.footer;
      if (embed.footerIcon) embedObject.footer.icon_url = embed.footerIcon;
    }
    if (embed.author || embed.authorIcon) {
      embedObject.author = {};
      if (embed.author) embedObject.author.name = embed.author;
      if (embed.authorIcon) embedObject.author.icon_url = embed.authorIcon;
    }
    if (embed.fields.length > 0) {
      embedObject.fields = embed.fields.filter(f => f.name && f.value);
    }

    return embedObject;
  };

  const generateWebhookPayload = () => {
    return {
      embeds: [generateJson()],
    };
  };

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(generateJson(), null, 2));
    setCopiedJson(true);
    toast.success('Embed JSON copied!');
    setTimeout(() => setCopiedJson(false), 2000);
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(JSON.stringify(generateWebhookPayload(), null, 2));
    setCopiedWebhook(true);
    toast.success('Webhook payload copied!');
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '88, 101, 242';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <MessageSquare className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Discord Embed Builder</h1>
          <p className="text-muted-foreground">
            Create beautiful embeds for Discord
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Form */}
        <div className="space-y-4">
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Embed Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    placeholder="Embed title"
                    value={embed.title}
                    onChange={(e) => updateEmbed('title', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={embed.color}
                      onChange={(e) => updateEmbed('color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={embed.color}
                      onChange={(e) => updateEmbed('color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Embed description (supports markdown)"
                  value={embed.description}
                  onChange={(e) => updateEmbed('description', e.target.value)}
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label>URL (clickable title)</Label>
                <Input
                  placeholder="https://example.com"
                  value={embed.url}
                  onChange={(e) => updateEmbed('url', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Images</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Thumbnail URL</Label>
                <Input
                  placeholder="Small image in top right"
                  value={embed.thumbnail}
                  onChange={(e) => updateEmbed('thumbnail', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Image URL</Label>
                <Input
                  placeholder="Large image at bottom"
                  value={embed.image}
                  onChange={(e) => updateEmbed('image', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Author & Footer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Author Name</Label>
                  <Input
                    placeholder="Author"
                    value={embed.author}
                    onChange={(e) => updateEmbed('author', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Author Icon URL</Label>
                  <Input
                    placeholder="Icon URL"
                    value={embed.authorIcon}
                    onChange={(e) => updateEmbed('authorIcon', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Footer Text</Label>
                  <Input
                    placeholder="Footer"
                    value={embed.footer}
                    onChange={(e) => updateEmbed('footer', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Footer Icon URL</Label>
                  <Input
                    placeholder="Icon URL"
                    value={embed.footerIcon}
                    onChange={(e) => updateEmbed('footerIcon', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Fields</span>
                <Button variant="outline" size="sm" onClick={addField} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Add Field
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {embed.fields.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No fields added yet
                </p>
              ) : (
                embed.fields.map((field, index) => (
                  <div key={index} className="p-3 rounded-lg bg-muted/30 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Field {index + 1}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeField(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Field name"
                      value={field.name}
                      onChange={(e) => updateField(index, 'name', e.target.value)}
                    />
                    <Textarea
                      placeholder="Field value"
                      value={field.value}
                      onChange={(e) => updateField(index, 'value', e.target.value)}
                      className="min-h-[60px]"
                    />
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={field.inline}
                        onChange={(e) => updateField(index, 'inline', e.target.checked)}
                        className="rounded"
                      />
                      Inline
                    </label>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview & Actions */}
        <div className="space-y-4">
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Discord-style embed preview */}
              <div
                className="rounded-lg overflow-hidden"
                style={{
                  borderLeft: `4px solid ${embed.color}`,
                  backgroundColor: 'rgb(47, 49, 54)',
                }}
              >
                <div className="p-4 space-y-2">
                  {/* Author */}
                  {(embed.author || embed.authorIcon) && (
                    <div className="flex items-center gap-2">
                      {embed.authorIcon && (
                        <img src={embed.authorIcon} alt="" className="w-6 h-6 rounded-full" />
                      )}
                      <span className="text-sm text-white">{embed.author}</span>
                    </div>
                  )}

                  {/* Title */}
                  {embed.title && (
                    <h3 className={`font-semibold text-[#00b0f4] ${embed.url ? 'hover:underline cursor-pointer' : ''}`}>
                      {embed.title}
                    </h3>
                  )}

                  {/* Description */}
                  {embed.description && (
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">
                      {embed.description}
                    </p>
                  )}

                  {/* Fields */}
                  {embed.fields.length > 0 && (
                    <div className="grid gap-2 mt-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                      {embed.fields.filter(f => f.name && f.value).map((field, i) => (
                        <div key={i} className={field.inline ? '' : 'col-span-full'}>
                          <p className="text-sm font-semibold text-white">{field.name}</p>
                          <p className="text-sm text-gray-300">{field.value}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Image */}
                  {embed.image && (
                    <img src={embed.image} alt="" className="max-w-full rounded mt-2" />
                  )}

                  {/* Footer */}
                  {(embed.footer || embed.footerIcon) && (
                    <div className="flex items-center gap-2 mt-2">
                      {embed.footerIcon && (
                        <img src={embed.footerIcon} alt="" className="w-5 h-5 rounded-full" />
                      )}
                      <span className="text-xs text-gray-400">{embed.footer}</span>
                    </div>
                  )}
                </div>

                {/* Thumbnail */}
                {embed.thumbnail && (
                  <img
                    src={embed.thumbnail}
                    alt=""
                    className="absolute top-4 right-4 w-20 h-20 rounded object-cover"
                    style={{ position: 'relative', float: 'right', marginTop: '-100px', marginRight: '16px' }}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button onClick={copyJson} className="flex-1 gap-2">
              {copiedJson ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              Copy JSON
            </Button>
            <Button onClick={copyWebhook} variant="outline" className="flex-1 gap-2">
              {copiedWebhook ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              Copy for Webhook
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscordEmbedBuilder;